import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  ArrowRight,
  ArrowLeft,
  Crown,
  Shield,
  Globe,
  Briefcase,
  Building2,
  TrendingUp,
  CheckCircle,
  Loader2,
  Lock,
  MapPin,
  Wallet,
} from 'lucide-react';
import { z } from 'zod';

const emailSchema = z.string().trim().email().max(255);
const phoneSchema = z.string().optional();

interface AssessmentStep {
  id: string;
  question: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  options: { value: string; label: string; sublabel?: string }[];
}

const steps: AssessmentStep[] = [
  {
    id: 'investment-goal',
    question: 'What is your primary investment objective?',
    subtitle: 'Understanding your goals helps us identify the perfect opportunities',
    icon: Briefcase,
    options: [
      { value: 'capital-growth', label: 'Capital Appreciation', sublabel: 'Long-term value growth' },
      { value: 'rental-yield', label: 'Rental Income', sublabel: 'Consistent cash flow' },
      { value: 'lifestyle', label: 'Lifestyle & Legacy', sublabel: 'Personal use & inheritance' },
      { value: 'diversification', label: 'Portfolio Diversification', sublabel: 'Risk mitigation' },
    ],
  },
  {
    id: 'markets',
    question: 'Which markets interest you most?',
    subtitle: 'Select all regions you\'d consider for investment',
    icon: Globe,
    options: [
      { value: 'monaco', label: 'Monaco', sublabel: 'Tax efficiency & prestige' },
      { value: 'dubai', label: 'Dubai', sublabel: 'Growth & luxury lifestyle' },
      { value: 'italy', label: 'Italy', sublabel: 'Heritage & lifestyle' },
      { value: 'spain', label: 'Spain', sublabel: 'Mediterranean living' },
    ],
  },
  {
    id: 'property-type',
    question: 'What type of property are you seeking?',
    subtitle: 'Each property type offers unique investment characteristics',
    icon: Building2,
    options: [
      { value: 'penthouse', label: 'Penthouse', sublabel: 'Exclusive city living' },
      { value: 'villa', label: 'Villa / Estate', sublabel: 'Privacy & space' },
      { value: 'waterfront', label: 'Waterfront Property', sublabel: 'Premium locations' },
      { value: 'any', label: 'Open to Opportunities', sublabel: 'Best value available' },
    ],
  },
  {
    id: 'timeline',
    question: 'What is your investment timeline?',
    subtitle: 'This helps us prioritize the most relevant opportunities',
    icon: TrendingUp,
    options: [
      { value: 'immediate', label: 'Ready to Invest', sublabel: 'Within 30 days' },
      { value: 'short', label: '1-3 Months', sublabel: 'Active search' },
      { value: 'medium', label: '3-6 Months', sublabel: 'Exploring options' },
      { value: 'long', label: '6+ Months', sublabel: 'Building knowledge' },
    ],
  },
  {
    id: 'budget',
    question: 'What is your investment range?',
    subtitle: 'All discussions are held in strict confidence',
    icon: Wallet,
    options: [
      { value: '2-5m', label: '€2M - €5M', sublabel: 'Entry luxury tier' },
      { value: '5-10m', label: '€5M - €10M', sublabel: 'Premium selection' },
      { value: '10-25m', label: '€10M - €25M', sublabel: 'Exclusive properties' },
      { value: '25m+', label: '€25M+', sublabel: 'Trophy assets' },
    ],
  },
];

interface InvestmentAssessmentProps {
  variant?: 'inline' | 'modal';
  onComplete?: () => void;
}

const InvestmentAssessment = ({ variant = 'inline', onComplete }: InvestmentAssessmentProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactInfo, setContactInfo] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const handleOptionSelect = (stepId: string, value: string) => {
    // For markets, allow multiple selection
    if (stepId === 'markets') {
      const currentMarkets = (answers.markets as string[]) || [];
      if (currentMarkets.includes(value)) {
        setAnswers({ ...answers, [stepId]: currentMarkets.filter((m) => m !== value) });
      } else {
        setAnswers({ ...answers, [stepId]: [...currentMarkets, value] });
      }
    } else {
      setAnswers({ ...answers, [stepId]: value });
      // Auto-advance after selection (except for markets)
      setTimeout(() => {
        if (currentStep < steps.length - 1) {
          setCurrentStep(currentStep + 1);
        } else {
          setShowContactForm(true);
        }
      }, 400);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowContactForm(true);
    }
  };

  const handleBack = () => {
    if (showContactForm) {
      setShowContactForm(false);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailValidation = emailSchema.safeParse(contactInfo.email);
    if (!emailValidation.success) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!contactInfo.name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setIsSubmitting(true);

    try {
      // Format the assessment answers into a readable message
      const assessmentSummary = Object.entries(answers)
        .map(([key, value]) => {
          const step = steps.find((s) => s.id === key);
          const stepName = step?.question || key;
          const valueStr = Array.isArray(value) ? value.join(', ') : value;
          return `${stepName}: ${valueStr}`;
        })
        .join('\n');

      const message = `Investment Assessment Results:\n\n${assessmentSummary}`;

      const { error } = await supabase.functions.invoke('submit-lead', {
        body: {
          name: contactInfo.name,
          email: emailValidation.data,
          phone: contactInfo.phone || undefined,
          message,
          source: 'investment-assessment',
          property_interest: (answers.markets as string[])?.join(', ') || 'Not specified',
        },
      });

      if (error) throw error;

      setIsComplete(true);
      toast.success('Thank you! We\'ll be in touch within 24 hours.');
      onComplete?.();
    } catch (error) {
      console.error('Assessment submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const progress = ((currentStep + 1) / (steps.length + 1)) * 100;
  const currentStepData = steps[currentStep];
  const StepIcon = currentStepData?.icon || Crown;

  if (isComplete) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-secondary to-background border border-gold/30 p-8 md:p-12 text-center"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-gold/10 via-transparent to-transparent" />
        <div className="relative z-10">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-20 h-20 rounded-full bg-gradient-gold flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="h-10 w-10 text-cream" />
          </motion.div>
          <h3 className="font-display text-3xl md:text-4xl text-foreground mb-4">
            Assessment Complete
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto mb-6">
            Thank you, {contactInfo.name}. A senior advisor will contact you within 24 hours to discuss
            your personalized investment strategy.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-gold">
            <Shield className="h-4 w-4" />
            <span>Your information is held in strict confidence</span>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-secondary via-card to-background border border-border/50 ${
        variant === 'modal' ? 'max-w-2xl mx-auto' : ''
      }`}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="relative z-10 p-6 md:p-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-gold flex items-center justify-center">
              <Crown className="h-5 w-5 text-cream" />
            </div>
            <div>
              <p className="text-sm font-medium text-gold uppercase tracking-wider">Private Assessment</p>
              <p className="text-xs text-muted-foreground">Confidential & Personalized</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" />
            <span>256-bit encrypted</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>Step {showContactForm ? steps.length + 1 : currentStep + 1} of {steps.length + 1}</span>
            <span>{Math.round(showContactForm ? 100 : progress)}% complete</span>
          </div>
          <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-gold rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${showContactForm ? 100 : progress}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!showContactForm ? (
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Question */}
              <div className="flex items-start gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <StepIcon className="h-6 w-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-display text-xl md:text-2xl text-foreground mb-1">
                    {currentStepData.question}
                  </h3>
                  <p className="text-sm text-muted-foreground">{currentStepData.subtitle}</p>
                </div>
              </div>

              {/* Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {currentStepData.options.map((option) => {
                  const isSelected =
                    currentStepData.id === 'markets'
                      ? ((answers.markets as string[]) || []).includes(option.value)
                      : answers[currentStepData.id] === option.value;

                  return (
                    <motion.button
                      key={option.value}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleOptionSelect(currentStepData.id, option.value)}
                      className={`relative p-4 rounded-xl border text-left transition-all duration-300 ${
                        isSelected
                          ? 'border-gold bg-gold/10 shadow-lg shadow-gold/10'
                          : 'border-border/50 bg-background/50 hover:border-gold/50 hover:bg-muted/30'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className={`font-medium ${isSelected ? 'text-gold' : 'text-foreground'}`}>
                            {option.label}
                          </p>
                          {option.sublabel && (
                            <p className="text-xs text-muted-foreground mt-0.5">{option.sublabel}</p>
                          )}
                        </div>
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-5 h-5 rounded-full bg-gradient-gold flex items-center justify-center"
                          >
                            <CheckCircle className="h-3.5 w-3.5 text-cream" />
                          </motion.div>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className="text-muted-foreground"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>

                {currentStepData.id === 'markets' && (
                  <Button
                    onClick={handleNext}
                    disabled={!answers.markets || (answers.markets as string[]).length === 0}
                    className="bg-gradient-gold text-cream hover:opacity-90"
                  >
                    Continue
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="contact-form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-start gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-display text-xl md:text-2xl text-foreground mb-1">
                    Receive Your Personalized Strategy
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    A senior advisor will contact you within 24 hours with tailored recommendations.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Full Name *</label>
                  <Input
                    type="text"
                    value={contactInfo.name}
                    onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                    placeholder="Your name"
                    className="h-12 bg-background/80 border-border"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email Address *</label>
                  <Input
                    type="email"
                    value={contactInfo.email}
                    onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                    placeholder="your@email.com"
                    className="h-12 bg-background/80 border-border"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Phone Number <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <Input
                    type="tel"
                    value={contactInfo.phone}
                    onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    className="h-12 bg-background/80 border-border"
                  />
                </div>

                <div className="flex items-center justify-between pt-4">
                  <Button variant="ghost" type="button" onClick={handleBack} className="text-muted-foreground">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-gold text-cream hover:opacity-90 px-8"
                    size="lg"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Get My Strategy
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground text-center pt-2">
                  By submitting, you agree to our{' '}
                  <a href="/privacy" className="text-gold hover:underline">
                    Privacy Policy
                  </a>
                  . Your data is protected and never shared.
                </p>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default InvestmentAssessment;
